import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Overline, PrimaryButton, OutlineButton } from "../components/UI";
import { SEO } from "../components/SEO";
import { ChevronDown } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  HIGH BILE ACIDS IN PREGNANCY (IHCP) — Master Implementation        */
/*  Content supplied by Dr. Tania Gurdip Singh (verbatim).             */
/*  Parts 1–5 loaded and passed all 27 final testing checks.           */
/*  Page FROZEN by user 2026-07-27. DO NOT MODIFY.                     */
/*  Medical-review line withheld pending explicit clinical sign-off.   */
/* ------------------------------------------------------------------ */

const PAGE_TITLE =
  "High Bile Acids in Pregnancy | IHCP Care | Dr Tania Gurdip Singh";
const PAGE_DESC =
  "Specialist care for high bile acids and intrahepatic cholestasis of pregnancy, including nutrition-led management, repeat testing, fetal-risk assessment and personalised timing of birth.";

/* Curated contents menu — 15 anchor labels in the exact order supplied.
   Anchor ids for sections not yet delivered will resolve once Parts 2–5
   are implemented. */
const TOC = [
  { id: "every-itch-needs-a-context", label: "Every Itch Needs a Context" },
  { id: "the-bile-acid-matters-more-than-the-itch", label: "The Bile Acid Matters More Than the Itch" },
  { id: "the-pattern-matters-more-than-one-report", label: "The Pattern Matters More Than One Report" },
  { id: "reconstructing-the-story", label: "Reconstructing the Story" },
  { id: "the-investigation-map", label: "The Investigation Map" },
  { id: "understanding-the-bile-acid-report", label: "Understanding the Bile Acid Report" },
  { id: "what-icp-can-mean-for-the-baby", label: "What ICP Can Mean for the Baby" },
  { id: "fetal-surveillance", label: "Fetal Surveillance" },
  { id: "treatment", label: "Treatment" },
  { id: "dr-tanias-nutrition-led-approach", label: "Dr Tania’s Nutrition-Led Approach" },
  { id: "timing-of-birth", label: "Timing of Birth" },
  { id: "labour-and-newborn-care", label: "Labour and Newborn Care" },
  { id: "the-next-pregnancy", label: "The Next Pregnancy" },
  { id: "faq", label: "Frequently Asked Questions" },
  { id: "disclaimer", label: "Medical Disclaimer" },
];

/* FAQs — used for BOTH visible accordion and FAQPage JSON-LD.
   Wording must match character-for-character between the two surfaces. */
const FAQ_ITEMS = [
  {
    q: "Does every pregnancy itch mean IHCP?",
    a: "No.\n\nItching is common in pregnancy.\n\nPersistent itching without a primary rash—especially involving the palms, soles or worsening at night—should prompt bile-acid and liver-function testing.",
  },
  {
    q: "Can I have IHCP when my first bile-acid test is normal?",
    a: "Yes.\n\nItching may begin before the blood tests become abnormal.\n\nIf symptoms persist, bile acids and liver-function tests should be repeated. [1,2]",
  },
  {
    q: "Do bile acids need to be tested fasting?",
    a: "Current RCOG, SOGC, SOMANZ and FIGO approaches support non-fasting bile-acid measurement for diagnosis or fetal-risk assessment.\n\nLaboratories may use different reference ranges.\n\nSerial results should be interpreted consistently. [1,4,5,9]",
  },
  {
    q: "What bile-acid level is considered IHCP?",
    a: "RCOG, SOGC and SOMANZ use non-fasting or random total bile acids of 19 µmol/L or more with otherwise unexplained pregnancy itching.\n\nSome laboratories and guidelines use different thresholds.\n\nThe result must be interpreted with the symptoms and laboratory method. [1,5,9]",
  },
  {
    q: "What is considered severe IHCP?",
    a: "A peak total bile-acid concentration of:\n\n100 µmol/L or more\n\nis classified as severe ICP by RCOG.\n\nThis is the level associated with the clearest increase in stillbirth risk. [1,2,6]",
  },
  {
    q: "My bile acids fell after treatment. Am I low risk again?",
    a: "Not automatically.\n\nThe highest level reached during the pregnancy remains important for fetal-risk assessment and delivery planning.\n\nA later fall does not erase an earlier severe result. [1,4–6]",
  },
  {
    q: "My bile acids improved with diet. Can delivery now be postponed?",
    a: "Not automatically.\n\nImprovement is encouraging and should be considered in the complete clinical assessment.\n\nHowever, delivery planning remains influenced by:\n\n- The highest bile-acid level reached.\n- Gestational age.\n- The rate of rise or fall.\n- Previous pregnancy history.\n- Singleton or multiple pregnancy.\n- Pre-eclampsia, diabetes or another complication.\n- Current maternal and fetal findings.\n\nA previous level of 100 µmol/L or more remains clinically important even when a later result is lower.",
  },
  {
    q: "Can liver enzymes be high when bile acids are normal?",
    a: "Yes.\n\nALT and AST may rise for many reasons.\n\nPersistent abnormal liver enzymes require appropriate investigation.\n\nICP fetal-risk assessment depends principally on bile acids, symptoms and exclusion of other causes.",
  },
  {
    q: "Can bile acids be high when liver enzymes are normal?",
    a: "Yes.\n\nBile acids may be abnormal even when routine liver-function tests are normal.\n\nThat is why bile acids must be measured specifically. [2]",
  },
  {
    q: "Can diet help reduce itching or bile acids?",
    a: "In my clinical practice, many women have shown meaningful improvement in itching and biochemical results after following a structured, individualised dietary plan.\n\nNutrition is therefore an active part of my management.\n\nScientific evidence has not yet established one universal diet that prevents stillbirth or removes the need for monitoring and delivery planning.\n\nThe response to diet is followed using symptoms, repeat bile acids, liver tests and the complete pregnancy picture. [1,4,11,12]",
  },
  {
    q: "Should I stop eating fat completely?",
    a: "No.\n\nSome women tolerate smaller amounts of lighter fats better than heavy, fried or very fatty meals.\n\nPregnancy still requires adequate calories and essential fatty acids.\n\nA zero-fat or crash diet may be nutritionally unsafe.\n\nFat intake should be individualised rather than eliminated, particularly when there is gallbladder disease, inadequate weight gain, diabetes or suspected fat malabsorption.\n\nHerbal “liver cleanses,” detox drinks and unregulated supplements should be avoided.",
  },
  {
    q: "Does ursodeoxycholic acid protect my baby from stillbirth?",
    a: "It has not been proved to prevent stillbirth.\n\nIt may modestly improve maternal itching and may reduce preterm birth in selected women.\n\nIt does not replace repeat testing or appropriately timed birth. [3,4,7,8]",
  },
  {
    q: "Will a normal CTG prove that my baby is safe?",
    a: "No.\n\nA normal CTG is reassuring for the period assessed.\n\nIt cannot eliminate the future risk associated with severe ICP.\n\nDelivery timing must not be replaced by repeated CTG reassurance.",
  },
  {
    q: "Do I need growth scans every week?",
    a: "Not solely because of otherwise uncomplicated ICP.\n\nAdditional ultrasound is performed when there is another indication, such as growth concerns, hypertension, diabetes, reduced movements or multiple pregnancy. [1,9]",
  },
  {
    q: "When should my baby be delivered if bile acids are above 100?",
    a: "RCOG and FIGO support planned birth at approximately 35–36 weeks.\n\nSMFM, endorsed by ACOG, recommends offering delivery at 36 weeks 0 days.\n\nThe final plan should consider the complete maternal and fetal situation. [1–4]",
  },
  {
    q: "When should my baby be delivered if bile acids are 40–99?",
    a: "RCOG recommends considering planned birth at approximately 38–39 weeks in an otherwise uncomplicated singleton pregnancy.\n\nSMFM recommends delivery within 36–39 weeks, generally toward the earlier end for levels of 40 µmol/L or more.\n\nThe decision is individualised. [1–3]",
  },
  {
    q: "Does IHCP mean I need a caesarean birth?",
    a: "No.\n\nICP alone does not require caesarean delivery.\n\nInduction and vaginal birth remain possible when there is no separate obstetric reason for caesarean birth.",
  },
  {
    q: "Can IHCP return in another pregnancy?",
    a: "Yes.\n\nRecurrence is common.\n\nBaseline liver-function tests and bile acids should be checked early in future pregnancies, with repeat testing if itching develops. [2]",
  },
];

/* Inline citation marker → jumps to first cited reference. */
function Cite({ children }) {
  const m = String(children).match(/(\d+)/);
  const first = m ? m[1] : "";
  return (
    <sup className="text-prestige font-medium">
      <a href={`#ref-${first}`} className="hover:underline">[{children}]</a>
    </sup>
  );
}

/* Accordion row (all closed by default). Uses whitespace-pre-line so the
   answer paragraphs and dash-lists render as they appear in the source data. */
function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  const slug = q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
  return (
    <div className="border-t border-line last:border-b" data-testid={`faq-${slug}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
      >
        <span className="font-serif text-lg md:text-xl text-navy leading-snug pt-0.5">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={1.5}
          className={`text-prestige shrink-0 mt-1 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="pb-6 pr-8 text-[15px] text-stone2 leading-relaxed max-w-3xl whitespace-pre-line">
          {a}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function HighBileAcidsIHCP() {
  const faqJsonLd = FAQ_ITEMS.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : undefined;

  return (
    <div data-testid="ihcp-page">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESC}
        jsonLd={faqJsonLd}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "High Bile Acids in Pregnancy (IHCP)", item: "/high-bile-acids-pregnancy-ihcp" },
        ]}
      />

      {/* Hero */}
      <section className="bg-bone" data-testid="ihcp-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <Overline>High Bile Acids in Pregnancy</Overline>
            <div className="h-px w-12 bg-prestige my-6" />
            <h1
              data-testid="ihcp-heading"
              className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-prestige tracking-tight"
            >
              When Itching Becomes a Fetal Risk Signal
            </h1>
            <p
              data-testid="ihcp-subheading"
              className="text-lg md:text-xl text-stone2 mt-6 leading-relaxed max-w-2xl"
            >
              Intrahepatic Cholestasis of Pregnancy is often first felt before
              it appears on a report.
            </p>
            <div className="text-[15px] text-stone2 mt-5 leading-relaxed max-w-2xl space-y-3">
              <p>The woman may notice relentless itching.</p>
              <p>Her first bile-acid result may still be normal.</p>
              <p>
                Later, the level may rise enough to change the safest timing of
                birth.
              </p>
              <p>No doctor can guarantee the outcome of a pregnancy.</p>
              <p>
                Expert care can do something more honest and clinically
                important:
              </p>
            </div>

            <ul
              className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed max-w-2xl"
              role="list"
            >
              {[
                "Listen to the symptom before dismissing it.",
                "Measure the right blood test.",
                "Follow the highest bile-acid level.",
                "Separate mild disease from severe disease.",
                "Use nutrition as active maternal management.",
                "Avoid false reassurance from a normal scan or CTG.",
                "Plan birth before the risk of remaining pregnant becomes greater than the risk of delivery.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-[15px] text-stone2 mt-6 leading-relaxed max-w-2xl">
              Nutrition is an active part of my management—not an afterthought
              added after the laboratory report.
            </p>

            <div className="mt-7 border-l-2 border-prestige pl-5 max-w-xl">
              <p className="text-[15px] text-navy leading-relaxed">
                My approach begins with three principles:
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-navy leading-relaxed">
                <li>
                  <strong>The itch raises the suspicion.</strong>
                </li>
                <li>
                  <strong>
                    Nutrition supports the mother and may improve the
                    biochemical pattern.
                  </strong>
                </li>
                <li>
                  <strong>
                    The peak bile-acid level and timing of birth guide fetal
                    safety.
                  </strong>
                </li>
              </ul>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton
                to="/contact?type=appointment&reason=ihcp"
                testid="ihcp-hero-cta-primary"
              >
                Schedule a High Bile Acids Consultation
              </PrimaryButton>
              <OutlineButton
                to="#fetal-surveillance"
                testid="ihcp-hero-cta-secondary"
              >
                Understand the Monitoring Plan
              </OutlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column: contents menu (sticky on desktop) + main body */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Contents menu */}
          <aside
            data-testid="ihcp-contents"
            className="lg:col-span-3 lg:sticky lg:top-32"
          >
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
              On this page
            </div>
            <div className="h-px w-8 bg-prestige mt-3 mb-4" />
            <nav aria-label="Contents">
              <ul className="space-y-2.5">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`toc-${t.id}`}
                      className="text-[14px] text-navy hover:text-prestige transition-colors leading-snug block"
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <div className="lg:col-span-9 max-w-3xl">
            {/* --- PART 1 START ---------------------------------------- */}

            {/* Every Itch Needs a Context */}
            <section id="every-itch-needs-a-context" className="scroll-mt-32">
              <Overline>Recognising the Signal</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Every Itch Needs a Context
              </h2>
              <div className="prose-rpl mt-5">
                <p>The condition is medically called:</p>
                <p>
                  <strong>Intrahepatic Cholestasis of Pregnancy</strong>, or{" "}
                  <strong>ICP</strong>.
                </p>
                <p>It is also commonly called:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "IHCP.",
                  "Obstetric cholestasis.",
                  "Pregnancy cholestasis.",
                  "Raised bile acids in pregnancy.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="prose-rpl mt-6">
                <p>ICP affects the way the liver handles bile during pregnancy.</p>
                <p>
                  Bile acids are produced in the liver and normally pass into
                  the intestine, where they help the body digest fats.
                </p>
                <p>In ICP, this process becomes impaired.</p>
                <p>
                  Bile acids accumulate in the maternal blood and may cross the
                  placenta.
                </p>
                <p>
                  The most recognisable symptom is itching without a primary
                  skin rash.
                </p>
                <p>The itching may:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Affect the palms of the hands.",
                  "Affect the soles of the feet.",
                  "Spread over the arms, legs, abdomen or entire body.",
                  "Become worse at night.",
                  "Disturb sleep severely.",
                  "Begin mildly and become intense.",
                  "Appear before the blood tests become abnormal.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="prose-rpl mt-6">
                <p>ICP usually develops later in pregnancy.</p>
                <p>It can begin earlier.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                There May Still Be Marks on the Skin
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP does not cause a primary rash.</p>
                <p>Scratching can cause:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Redness.",
                  "Excoriations.",
                  "Broken skin.",
                  "Scabs.",
                  "Bruising.",
                  "Secondary infection.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A woman may also have eczema, an allergy or another skin
                  disorder at the same time.
                </p>
                <p>Visible scratch marks do not exclude ICP.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Itching Is Common. ICP Is Not.
              </h3>
              <div className="prose-rpl mt-4">
                <p>Many pregnant women experience itching.</p>
                <p>Most do not have ICP.</p>
                <p>
                  However, persistent, generalised or nocturnal itching—
                  especially involving the palms or soles—deserves assessment.
                </p>
                <p>The symptom should not be dismissed merely because:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "There is no jaundice.",
                  "The first liver-function test is normal.",
                  "The first bile-acid result is normal.",
                  "The baby is moving normally.",
                  "An ultrasound is reassuring.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Some women experience itching for days or weeks before their
                  blood results become abnormal.
                </p>
                <p>
                  If the itching continues, the tests should be repeated.
                  <Cite>1–5</Cite>
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    A normal bile-acid result answers the question for the day
                    the blood was taken. It does not permanently exclude ICP.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Symptoms That Need a Broader Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP is an important diagnosis.</p>
                <p>
                  It is not the only possible cause of itching or abnormal
                  liver tests in pregnancy.
                </p>
                <p>A broader assessment is particularly important when there is:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Very early onset.",
                  "Severe disease early in pregnancy.",
                  "Fever.",
                  "Significant abdominal or right-upper-quadrant pain.",
                  "Persistent vomiting.",
                  "Jaundice.",
                  "Dark urine.",
                  "Pale stools.",
                  "Loss of appetite.",
                  "Marked weakness.",
                  "High blood pressure.",
                  "Severe headache.",
                  "Visual disturbance.",
                  "Rapid swelling.",
                  "Low platelets.",
                  "Significant bilirubin elevation.",
                  "Symptoms continuing after birth.",
                  "Known liver or gallbladder disease.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                These features may indicate another condition or a second
                condition occurring alongside ICP.
              </p>
            </section>

            {/* The Bile Acid Matters More Than the Itch */}
            <section
              id="the-bile-acid-matters-more-than-the-itch"
              className="scroll-mt-32 mt-16"
            >
              <Overline>Severity Classification</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Bile Acid Matters More Than the Itch
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The severity of itching does not reliably predict the
                  bile-acid level.
                </p>
                <p>
                  A woman with intense itching may have a modest elevation.
                </p>
                <p>
                  Another woman with less dramatic itching may have severe
                  biochemical disease.
                </p>
                <p>The itching deserves treatment.</p>
                <p>The bile-acid concentration guides fetal-risk assessment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Current Severity Categories
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Using the RCOG classification based on the{" "}
                  <strong>highest recorded bile-acid level</strong>:
                </p>
              </div>

              <div className="mt-6 grid md:grid-cols-3 gap-5">
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Mild ICP
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    19–39 µmol/L
                  </div>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Moderate ICP
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    40–99 µmol/L
                  </div>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Severe ICP
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    100 µmol/L or more
                  </div>
                </div>
              </div>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                These categories must be interpreted together with:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Gestational age.",
                  "Singleton or multiple pregnancy.",
                  "Previous ICP.",
                  "Previous stillbirth.",
                  "Pre-eclampsia.",
                  "Gestational diabetes.",
                  "Fetal growth concerns.",
                  "Other maternal medical conditions.",
                  "The trend in bile-acid results.",
                ].map((item, idx, arr) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>
                      {item}
                      {idx === arr.length - 1 && (
                        <>
                          {" "}
                          <Cite>1,2</Cite>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Highest Level Matters
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids can fluctuate.</p>
                <p>A later result may fall because of:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Natural biological variation.",
                  "Timing of the blood sample.",
                  "Treatment.",
                  "Laboratory variation.",
                  "Changes in food intake.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A fall in the result does not erase the fact that a higher
                  concentration occurred earlier.
                </p>
                <p>
                  Fetal-risk assessment and timing of birth should consider
                  the <strong>peak bile-acid concentration during the pregnancy</strong>,
                  not only the latest report.<Cite>1,4–6</Cite>
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    A falling result can be encouraging. It does not rewrite
                    the pregnancy’s highest-risk category.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Mild ICP: 19–39 µmol/L
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  In an otherwise uncomplicated singleton pregnancy, the
                  stillbirth risk appears similar to the background pregnancy
                  risk.
                </p>
                <p>This does not mean that the diagnosis should be ignored.</p>
                <p>The woman still requires:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Repeat bile-acid testing.",
                  "Review of symptoms.",
                  "Assessment for associated conditions.",
                  "An individualised birth plan.",
                  "Immediate assessment if fetal movements reduce or change.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The bile-acid level may remain mild.</p>
                <p>It may also rise later.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Moderate ICP: 40–99 µmol/L
              </h3>
              <div className="prose-rpl mt-4">
                <p>At this level, the risks of the following become more relevant:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Spontaneous preterm birth.",
                  "Meconium-stained amniotic fluid.",
                  "Neonatal-unit admission.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  In an otherwise uncomplicated singleton pregnancy, the
                  stillbirth risk appears similar to background risk until
                  approximately 38–39 weeks.
                </p>
                <p>
                  This is why planned birth is usually discussed before or
                  around that stage rather than routinely waiting beyond it.
                  <Cite>1,2,5,6</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Severe ICP: 100 µmol/L or More
              </h3>
              <div className="prose-rpl mt-4">
                <p>This is the level at which the stillbirth risk rises substantially.</p>
                <p>
                  RCOG patient information estimates that approximately 3 in
                  100 women with severe ICP may experience stillbirth after 36
                  weeks.
                </p>
                <p>
                  Large international data show a marked increase in risk when
                  the peak bile-acid concentration reaches 100 µmol/L or more.
                </p>
                <p>
                  The increase becomes particularly important from
                  approximately 35–36 weeks.
                </p>
                <p>
                  This is why major international guidelines recommend planned
                  preterm or early-term birth rather than routinely waiting
                  until the due date.<Cite>1–6</Cite>
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    At 100 µmol/L or more, the conversation changes from
                    routine observation to active prevention through the
                    timing of birth.
                  </strong>
                </p>
              </div>
            </section>

            {/* The Pattern Matters More Than One Report */}
            <section
              id="the-pattern-matters-more-than-one-report"
              className="scroll-mt-32 mt-16"
            >
              <Overline>Individualising the Plan</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Pattern Matters More Than One Report
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Two women may both have a bile-acid result of 45 µmol/L.
                </p>
                <p>They may not require identical care.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Itching With Normal Bile Acids
              </h3>
              <div className="prose-rpl mt-4">
                <p>This may mean:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "ICP has not developed.",
                  "The blood was taken before the bile acids rose.",
                  "Another skin or medical condition is causing the itching.",
                  "Symptoms require repeat testing.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  If itching persists and no other explanation is found, bile
                  acids and liver tests should be repeated.
                </p>
                <p>
                  Treatment should not permanently replace diagnostic
                  confirmation.
                </p>
                <p>
                  Where clinically appropriate, the initial blood sample
                  should be collected before ursodeoxycholic acid is started.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Bile Acids of 19–39 µmol/L Near Term
              </h3>
              <div className="prose-rpl mt-4">
                <p>This often allows a later birth plan than severe ICP.</p>
                <p>The decision still depends on:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Whether the level remains stable.",
                  "Other pregnancy complications.",
                  "Previous history.",
                  "Fetal movements.",
                  "Patient preference.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Bile Acids of 40–99 µmol/L
              </h3>
              <div className="prose-rpl mt-4">
                <p>This level requires closer attention to:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The biochemical trend.",
                  "Gestational age.",
                  "Associated pre-eclampsia or diabetes.",
                  "Multiple pregnancy.",
                  "Previous severe ICP.",
                  "Planned timing of birth.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Bile Acids of 100 µmol/L or More
              </h3>
              <div className="prose-rpl mt-4">
                <p>This level should prompt specialist-led delivery planning.</p>
                <p>
                  Waiting for the bile acid to fall before making a plan may
                  create false reassurance.
                </p>
                <p>
                  The peak value remains clinically relevant even if treatment
                  or dietary management lowers a later result.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Severe Disease Before 28 Weeks
              </h3>
              <div className="prose-rpl mt-4">
                <p>Early-onset severe ICP is unusual.</p>
                <p>It should prompt consideration of:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Underlying liver disease.",
                  "Gallstones or biliary obstruction.",
                  "Viral hepatitis.",
                  "Autoimmune liver disease.",
                  "Drug-related liver injury.",
                  "Genetic susceptibility affecting bile transport.",
                  "Multiple pregnancy or very high hormonal exposure.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Obstetric, fetal-medicine and hepatology input may be required.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Previous ICP Pregnancy
              </h3>
              <div className="prose-rpl mt-4">
                <p>A previous pregnancy may tell us:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "How early symptoms began.",
                  "How high the bile acids became.",
                  "Whether they rose rapidly.",
                  "Whether early delivery was required.",
                  "Whether meconium was present.",
                  "Whether the baby required neonatal care.",
                  "Whether a fetal loss occurred.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The current pregnancy should not be managed as though that
                history does not exist.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                ICP With Twins or Triplets
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Multiple pregnancy already carries additional maternal and
                  fetal risks.
                </p>
                <p>ICP may begin earlier and may be more severe.</p>
                <p>
                  Delivery timing cannot be copied directly from an
                  uncomplicated singleton pregnancy.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                ICP With Pre-eclampsia or Gestational Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Both conditions may alter maternal and fetal risk.
                </p>
                <p>
                  The final plan should address the complete pregnancy—not the
                  bile-acid result in isolation.
                </p>
              </div>
            </section>

            {/* --- PART 1 END --------------------------------------- */}

            {/* --- PART 2 START ------------------------------------- */}

            {/* Reconstructing the Story */}
            <section id="reconstructing-the-story" className="scroll-mt-32 mt-16">
              <Overline>The First Consultation</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Before Ordering Tests, Reconstruct the Story
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  The first consultation should not begin and end with the
                  latest laboratory value.
                </p>
                <p>
                  It should reconstruct the symptom, the biochemical pattern,
                  nutrition, maternal health and the complete pregnancy risk.
                </p>
                <p>I review:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "When the itching began.",
                  "Where it began.",
                  "Whether the palms or soles are involved.",
                  "Whether it is worse at night.",
                  "Whether there is a primary rash.",
                  "Whether sleep has become severely disturbed.",
                  "Whether jaundice is present.",
                  "Urine and stool colour.",
                  "Abdominal pain.",
                  "Nausea or vomiting.",
                  "Appetite and weight changes.",
                  "Current medicines and supplements.",
                  "Antibiotics or hormonal medicines used recently.",
                  "Previous gallstones.",
                  "Previous liver disease.",
                  "Previous hepatitis.",
                  "Autoimmune disease.",
                  "Family history of ICP or liver disease.",
                  "Previous ICP pregnancies.",
                  "The peak bile-acid level in each pregnancy.",
                  "The gestational age at which the previous level rose.",
                  "Previous spontaneous or planned preterm birth.",
                  "Previous stillbirth or neonatal complications.",
                  "Whether the current pregnancy is singleton or multiple.",
                  "IVF or ovarian-stimulation history.",
                  "Current blood pressure.",
                  "Symptoms of pre-eclampsia.",
                  "Gestational diabetes.",
                  "Fetal growth and movement pattern.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The nutrition history includes:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Typical meal timing.",
                  "Long fasting intervals.",
                  "Fried and high-fat food intake.",
                  "Recent major dietary changes.",
                  "Vegetarian or non-vegetarian food pattern.",
                  "Protein intake.",
                  "Fibre intake and bowel pattern.",
                  "Hydration.",
                  "Herbal products, powders, teas or “detox” preparations.",
                  "Food intolerance after the itching began.",
                  "Whether itching or digestive discomfort changes after particular meals.",
                  "Changes in symptoms or bile acids after a structured dietary intervention.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                For every bile-acid result, I review:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Date.",
                  "Gestational age.",
                  "Whether the sample was fasting or non-fasting.",
                  "The laboratory reference range.",
                  "Whether ursodeoxycholic acid had already been started.",
                  "The accompanying liver-function results.",
                  "The trend from previous samples.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                A dietary history is reviewed as carefully as the medication
                history because both may influence maternal symptoms,
                metabolic health and interpretation of the biochemical trend.
              </p>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    One number tells us the concentration. The timeline tells
                    us the disease.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What I Ask Patients to Bring
              </h3>
              <div className="prose-rpl mt-4">
                <p>Please bring every available:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Bile-acid report.",
                  "Liver-function test.",
                  "Bilirubin report.",
                  "Coagulation report.",
                  "Blood-pressure record.",
                  "Glucose or gestational-diabetes report.",
                  "Ultrasound and growth-scan report.",
                  "CTG or fetal-monitoring report.",
                  "Previous ICP pregnancy records.",
                  "Previous delivery summary.",
                  "Neonatal discharge summary.",
                  "Liver or gallbladder ultrasound.",
                  "Hepatitis or autoimmune-liver test.",
                  "Complete medicine and supplement list.",
                  "A three-day food and meal-timing record, when possible.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A chronological list of bile-acid results with their
                  gestational dates is particularly valuable.
                </p>
                <p>The food record is not used to blame the patient.</p>
                <p>
                  It helps identify correctable patterns and allows the
                  dietary plan to remain nutritionally adequate.
                </p>
                <p>Missing documents should never prevent consultation.</p>
                <p>
                  The history can still be reconstructed from the information
                  available.
                </p>
              </div>
            </section>

            {/* The Investigation Map */}
            <section id="the-investigation-map" className="scroll-mt-32 mt-16">
              <Overline>Investigations</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                An Investigation Map — Not a Shopping List
              </h2>
              <div className="prose-rpl mt-5">
                <p>The purpose of testing is to:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Confirm or challenge the diagnosis.",
                  "Identify the highest level of risk.",
                  "Exclude another liver disorder when indicated.",
                  "Decide how frequently testing should be repeated.",
                  "Plan the safest timing of birth.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                1. Total Serum Bile Acids
              </h3>
              <div className="prose-rpl mt-4">
                <p>This is the central laboratory test.</p>
                <p>
                  RCOG, SOGC and SOMANZ support a non-fasting or random
                  threshold of:
                </p>
                <p>
                  <strong>19 µmol/L or more</strong>
                </p>
                <p>in a pregnant woman with otherwise unexplained itching.</p>
                <p>Other guidelines and laboratories may use different thresholds.</p>
                <p>The result should therefore be interpreted using:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The clinical symptoms.",
                  "The pregnancy-specific reference range.",
                  "Whether the sample was fasting or non-fasting.",
                  "The method used by the laboratory.",
                  "Repeat measurements.",
                ].map((item, idx, arr) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>
                      {item}
                      {idx === arr.length - 1 && (
                        <>
                          {" "}
                          <Cite>1,5,9</Cite>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                2. Liver-Function Tests
              </h3>
              <div className="prose-rpl mt-4">
                <p>These commonly include:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "ALT.",
                  "AST.",
                  "Bilirubin.",
                  "GGT.",
                  "Albumin.",
                  "Alkaline phosphatase.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>ALT and AST may be significantly raised in ICP.</p>
                <p>They may also remain normal.</p>
                <p>
                  Bile acids may be abnormal even when routine liver enzymes
                  are normal.
                </p>
                <p>ALT and AST help assess maternal liver involvement.</p>
                <p>
                  They do not predict fetal risk as reliably as the peak
                  bile-acid concentration.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                3. Alkaline Phosphatase Requires Caution
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Alkaline phosphatase naturally rises during pregnancy
                  because the placenta produces it.
                </p>
                <p>
                  A raised alkaline phosphatase result alone is therefore not
                  useful for diagnosing or grading ICP.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                4. Bilirubin
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bilirubin is often normal.</p>
                <p>
                  Jaundice and substantial bilirubin elevation are less
                  typical.
                </p>
                <p>They require assessment for:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "More severe cholestasis.",
                  "Biliary obstruction.",
                  "Viral hepatitis.",
                  "Another liver disorder.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                5. Blood Count and Platelets
              </h3>
              <div className="prose-rpl mt-4">
                <p>These may help identify:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Infection.",
                  "Anaemia.",
                  "Thrombocytopenia.",
                  "HELLP syndrome.",
                  "Another pregnancy complication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                6. Blood Pressure and Urine Protein
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  ICP is associated with an increased likelihood of
                  pre-eclampsia.
                </p>
                <p>
                  Blood pressure and appropriate urine or laboratory
                  assessment should not be overlooked.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                7. Glucose Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>Gestational diabetes is more common in women with ICP.</p>
                <p>
                  Testing should follow the woman’s gestational age, previous
                  results and risk factors.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                8. Coagulation Testing
              </h3>
              <div className="prose-rpl mt-4">
                <p>Most women with ICP do not develop a clotting disorder.</p>
                <p>Prothrombin time or INR may be considered when there is:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Jaundice.",
                  "Severe or prolonged cholestasis.",
                  "Pale stools.",
                  "Suspected fat malabsorption.",
                  "Significant bleeding.",
                  "Another liver disorder.",
                  "Concern about vitamin K deficiency.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                9. Liver and Gallbladder Imaging
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  An ultrasound of the liver or gallbladder is not required
                  for every straightforward case.
                </p>
                <p>It may be appropriate when there is:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Significant abdominal pain.",
                  "Jaundice.",
                  "Very early disease.",
                  "Severe biochemical abnormality.",
                  "Atypical symptoms.",
                  "Persistent abnormal tests after birth.",
                  "Previous gallstones or biliary disease.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                10. Additional Liver Investigations
              </h3>
              <div className="prose-rpl mt-4">
                <p>Selected women may require testing for:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Viral hepatitis.",
                  "Autoimmune liver disease.",
                  "Biliary obstruction.",
                  "Drug-induced liver injury.",
                  "Primary biliary cholangitis.",
                  "Primary sclerosing cholangitis.",
                  "Metabolic or genetic liver conditions.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>These are not universal ICP panels.</p>
                <p>They are selected according to the clinical presentation.</p>
              </div>
            </section>

            {/* Understanding the Bile Acid Report */}
            <section
              id="understanding-the-bile-acid-report"
              className="scroll-mt-32 mt-16"
            >
              <Overline>Reading the Numbers</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Understanding the Bile Acid Report
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What Does µmol/L Mean?
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids are usually reported in:</p>
                <p>
                  <strong>Micromoles per litre</strong>, written as{" "}
                  <strong>µmol/L</strong>.
                </p>
                <p>
                  The number measures the concentration in the maternal blood
                  sample.
                </p>
                <p>
                  It does not directly measure the concentration around the
                  baby.
                </p>
                <p>It does not directly measure fetal wellbeing.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fasting and Non-Fasting Results Are Not Automatically Equivalent
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids normally rise after eating.</p>
                <p>
                  A fasting result may be lower than a non-fasting result
                  obtained on the same day.
                </p>
                <p>
                  Modern risk-stratification guidance increasingly uses
                  non-fasting measurements to avoid missing clinically
                  important peaks.
                </p>
                <p>
                  Serial measurements should ideally be interpreted
                  consistently.
                </p>
                <p>
                  A fasting result from one laboratory should not be compared
                  casually with a non-fasting result from another without
                  context.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Laboratory Reference Range Matters
              </h3>
              <div className="prose-rpl mt-4">
                <p>Different laboratories may use:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Different assay platforms.",
                  "Different upper limits.",
                  "Different fasting instructions.",
                  "Different reporting systems.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                A result should not be interpreted using a copied internet
                threshold without checking the laboratory method.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Repeat Testing Is Essential
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids may rise rapidly.</p>
                <p>
                  After diagnosis, blood tests are commonly repeated after
                  approximately one week.
                </p>
                <p>The frequency may increase according to:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Gestational age.",
                  "Disease severity.",
                  "Previous rate of rise.",
                  "Symptoms.",
                  "Other pregnancy complications.",
                  "Proximity to the planned delivery date.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The FIGO guideline advises at least weekly non-fasting
                bile-acid measurement from 32 weeks in confirmed ICP to
                identify clinically important rises.<Cite>4</Cite>
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Small Change May Not Be Clinically Significant
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  A change from 22 to 25 µmol/L is different from a rise from
                  25 to 105 µmol/L.
                </p>
                <p>Interpretation should consider:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Laboratory variation.",
                  "Sample timing.",
                  "Treatment.",
                  "The overall trajectory.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                A Falling Level Does Not Mean the Condition Has Disappeared
              </h3>
              <div className="prose-rpl mt-4">
                <p>The level can fluctuate.</p>
                <p>
                  Risk stratification remains linked to the highest recorded
                  concentration.
                </p>
                <p>
                  A woman whose level reached 110 µmol/L should not
                  automatically be reclassified as mild because a later
                  result is 32 µmol/L.
                </p>
                <p>
                  A fall after dietary treatment is clinically encouraging and
                  may support continuation of the individualised plan.
                </p>
                <p>It should be documented as a treatment response.</p>
                <p>
                  It must not be used to retrospectively delete a previous
                  severe result or postpone an otherwise indicated delivery
                  without complete clinical reassessment.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The ALT Is Not the Delivery Number
              </h3>
              <div className="prose-rpl mt-4">
                <p>A very high ALT deserves maternal assessment.</p>
                <p>
                  It is not used in the same way as bile acids to estimate
                  stillbirth risk.
                </p>
                <p>
                  A normal ALT does not make a bile-acid concentration of 120
                  µmol/L low risk.
                </p>
              </div>
            </section>

            {/* What ICP Can Mean for the Baby */}
            <section
              id="what-icp-can-mean-for-the-baby"
              className="scroll-mt-32 mt-16"
            >
              <Overline>Fetal Implications</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                What ICP Can Mean for the Baby
              </h2>
              <div className="prose-rpl mt-5">
                <p>Most women with ICP have a healthy baby.</p>
                <p>
                  The purpose of specialist care is to identify pregnancies in
                  which fetal risk has become meaningfully higher.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Spontaneous Preterm Birth
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  ICP is associated with an increased chance of labour
                  beginning before 37 weeks.
                </p>
                <p>
                  The risk becomes more relevant when bile acids reach 40
                  µmol/L or more.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Planned Preterm or Early-Term Birth
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Some babies are born early because the clinical team
                  recommends delivery before the risk of continuing pregnancy
                  rises further.
                </p>
                <p>This is medically indicated birth.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Meconium-Stained Amniotic Fluid
              </h3>
              <div className="prose-rpl mt-4">
                <p>The baby may pass meconium before birth.</p>
                <p>The amniotic fluid may become green or brown.</p>
                <p>The risk increases with more severe ICP.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Neonatal-Unit Admission
              </h3>
              <div className="prose-rpl mt-4">
                <p>Admission may be required because of:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Prematurity.",
                  "Breathing difficulty.",
                  "Meconium exposure.",
                  "Another pregnancy complication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Stillbirth
              </h3>
              <div className="prose-rpl mt-4">
                <p>The risk is not equal at every bile-acid level.</p>
                <p>
                  The major increase occurs when the peak total bile-acid
                  concentration reaches:
                </p>
                <p>
                  <strong>100 µmol/L or more</strong>
                </p>
                <p>
                  The risk rises particularly after approximately 35–36 weeks.
                </p>
                <p>
                  This is why delivery timing—not simply medication, diet or
                  surveillance—forms the central fetal-protection strategy in
                  severe ICP.<Cite>1–6</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Other Risk Factors Matter
              </h3>
              <div className="prose-rpl mt-4">
                <p>The final risk may be higher when ICP occurs with:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Multiple pregnancy.",
                  "Pre-eclampsia.",
                  "Gestational diabetes.",
                  "Fetal growth restriction.",
                  "Previous ICP-related fetal loss.",
                  "Another maternal or fetal condition.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Fetal Surveillance */}
            <section id="fetal-surveillance" className="scroll-mt-32 mt-16">
              <Overline>Monitoring</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Fetal Surveillance
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What Monitoring Can Do
              </h3>
              <div className="prose-rpl mt-4">
                <p>Fetal surveillance can:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Assess the fetal heart-rate pattern at the time of testing.",
                  "Identify another obstetric concern.",
                  "Support decisions when fetal movements change.",
                  "Provide information when delivery would be considered for an abnormal result.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What Monitoring Cannot Do
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  A normal CTG cannot prove that the baby will remain safe
                  until the next appointment.
                </p>
                <p>
                  A normal ultrasound cannot remove the risk associated with
                  severe bile-acid elevation.
                </p>
                <p>
                  There is no antenatal test that reliably predicts every
                  ICP-related stillbirth.
                </p>
                <p>
                  This is one reason severe ICP is not managed by repeated
                  reassurance alone.
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    Monitoring observes the baby. It does not neutralise the
                    bile-acid risk.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Where Guidelines Differ
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  SMFM suggests beginning antenatal fetal surveillance at a
                  gestational age when the clinical team would act on an
                  abnormal result, or at diagnosis when ICP is diagnosed later.
                </p>
                <p>
                  RCOG does not recommend additional growth scans solely
                  because a woman has ICP.
                </p>
                <p>
                  SOMANZ similarly states that routine growth scans and CTG
                  are not required in otherwise uncomplicated ICP.
                </p>
                <p>
                  These positions recognise the limitations of fetal testing
                  and the importance of selecting surveillance according to
                  the complete pregnancy picture.<Cite>1–3,9</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                When Ultrasound Is Appropriate
              </h3>
              <div className="prose-rpl mt-4">
                <p>An ultrasound may be required because of:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Reduced fetal movements.",
                  "Suspected growth restriction.",
                  "Multiple pregnancy.",
                  "Hypertension or pre-eclampsia.",
                  "Gestational diabetes.",
                  "Abnormal fundal-height measurement.",
                  "Another obstetric indication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fetal Movements
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The mother should remain attentive to her baby’s usual
                  movement pattern.
                </p>
                <p>She should seek immediate maternity assessment if:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Movements reduce.",
                  "Movements stop.",
                  "The pattern changes significantly.",
                  "She feels that something is wrong.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                She should not wait for:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The next clinic appointment.",
                  "The next bile-acid test.",
                  "The next scheduled CTG.",
                  "The itching to worsen.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                A movement-counting application or home Doppler should not
                replace professional assessment.
              </p>
            </section>

            {/* --- PART 2 END --------------------------------------- */}

            {/* --- PART 3 START ------------------------------------- */}

            {/* Treatment Must Follow the Purpose */}
            <section id="treatment" className="scroll-mt-32 mt-16">
              <Overline>Treatment</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Treatment Must Follow the Purpose
              </h2>
              <div className="prose-rpl mt-5">
                <p>IHCP treatment has more than one purpose.</p>
                <p>One part aims to reduce itching and restore sleep.</p>
                <p>
                  Another addresses maternal nutrition, digestion, metabolic
                  health and the biochemical pattern.
                </p>
                <p>
                  The final—and most important fetal-safety pathway—is based
                  on repeat bile-acid measurement, recognition of additional
                  pregnancy risks and appropriately timed birth.
                </p>
                <p>In my practice, nutrition begins early.</p>
                <p>It does not wait until every medicine has failed.</p>
                <p>Treatment is divided into four groups.</p>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Group 1
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    Nutrition-Led Maternal Management
                  </div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Uses an individualised food plan to reduce avoidable
                    dietary stress, maintain adequate pregnancy nutrition and
                    support maternal metabolic and liver health.
                  </p>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Group 2
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    Treatment for Maternal Symptoms
                  </div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Aims to reduce itching and improve sleep.
                  </p>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Group 3
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    Treatment of Associated Maternal Problems
                  </div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Addresses vitamin deficiency, gestational diabetes,
                    pre-eclampsia, gallbladder disease or another liver
                    disorder when present.
                  </p>
                </div>
                <div className="bg-surfacealt/60 border-l-2 border-prestige p-5">
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    Group 4
                  </div>
                  <div className="font-serif text-xl text-navy mt-2 leading-tight">
                    Fetal-Risk Reduction
                  </div>
                  <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                    Relies principally on the items listed below.
                  </p>
                </div>
              </div>

              <ul
                className="mt-6 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Correct disease classification.",
                  "Repeat bile-acid measurement.",
                  "Recognition of additional risk factors.",
                  "Attention to fetal movements.",
                  "Appropriately timed birth.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Dr Tania's Nutrition-Led Approach */}
            <section
              id="dr-tanias-nutrition-led-approach"
              className="scroll-mt-32 mt-16"
            >
              <Overline>Nutrition</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Dr Tania’s Nutrition-Led Approach
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Nutrition Is Active Clinical Management
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  In my practice, food is not treated as a decorative lifestyle
                  recommendation.
                </p>
                <p>
                  A detailed dietary history is taken alongside the history of
                  itching, bile-acid values, liver enzymes, weight, digestion,
                  glucose levels, medicines and pregnancy complications.
                </p>
                <p>
                  I have cared for many women whose itching and laboratory
                  pattern improved substantially after a structured and
                  individualised dietary programme.
                </p>
                <p>
                  In selected women, symptoms and biochemical results have
                  remained controlled without requiring medication solely for
                  symptom relief.
                </p>
                <p>This clinical experience is important.</p>
                <p>It must also be interpreted honestly.</p>
                <p>
                  An improvement in itching, ALT or bile acids does not prove
                  that fetal risk has disappeared.
                </p>
                <p>
                  The highest bile-acid concentration reached during the
                  pregnancy remains relevant when planning surveillance and
                  birth.
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    Nutrition may improve the maternal and biochemical picture.
                    It cannot, by itself, certify that the baby is safe.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What the Nutrition Plan Is Designed to Achieve
              </h3>
              <div className="prose-rpl mt-4">
                <p>The plan is designed to:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Avoid prolonged fasting and irregular eating.",
                  "Maintain adequate pregnancy calories and protein.",
                  "Reduce heavy, deep-fried and repeatedly reheated foods.",
                  "Reduce excessive saturated fat and ultra-processed food.",
                  "Avoid large concentrated sugar loads when metabolic risk is present.",
                  "Use lighter cooking methods.",
                  "Increase suitable whole-food fibre gradually.",
                  "Maintain appropriate hydration.",
                  "Support regular bowel function.",
                  "Correct identified nutritional deficiencies safely.",
                  "Coordinate nutrition with gestational-diabetes or weight management when required.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The purpose is not to place every patient on the same
                  printed “liver diet.”
                </p>
                <p>The plan must consider:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Vegetarian or non-vegetarian food preferences.",
                  "Cultural and regional diet.",
                  "Gestational diabetes.",
                  "Maternal weight and pregnancy weight gain.",
                  "Nausea, vomiting or reflux.",
                  "Gallstones or fat intolerance.",
                  "Pale stools or suspected malabsorption.",
                  "Anaemia and protein intake.",
                  "Twin or higher-order pregnancy.",
                  "Food affordability and availability.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fat Should Be Individualised—not Eliminated
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids help digest fats and fat-soluble vitamins.</p>
                <p>
                  Some women notice that heavy or very fatty meals aggravate
                  digestive discomfort.
                </p>
                <p>
                  This does not mean that every form of dietary fat should be
                  removed.
                </p>
                <p>
                  Pregnancy still requires adequate energy and essential fatty
                  acids.
                </p>
                <p>
                  Extreme fat restriction, crash dieting or an unbalanced
                  liquid diet may create nutritional deficiency.
                </p>
                <p>
                  The amount, type and distribution of fat should therefore be
                  adjusted individually rather than eliminated indiscriminately.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fibre Has Biological Plausibility—but Is Not a Universal Prescription
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Dietary fibre can influence bowel transit and the intestinal
                  handling of bile acids.
                </p>
                <p>
                  A small randomised trial evaluated guar gum in women with ICP.
                </p>
                <p>
                  The trial involved only 39 women, and the subsequent Cochrane
                  assessment found insufficient evidence to recommend guar gum
                  as a routine treatment for all women with ICP.
                </p>
                <p>
                  Whole-food fibre may be used thoughtfully within a balanced
                  plan.
                </p>
                <p>
                  Concentrated fibre preparations should not be self-prescribed
                  as though they were proven fetal therapy.<Cite>11,12</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What I Do Not Recommend
              </h3>
              <div className="prose-rpl mt-4">
                <p>I do not recommend:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Starvation or prolonged fasting.",
                  "Zero-fat diets.",
                  "Crash weight-loss diets.",
                  "Juice-only plans.",
                  "“Liver detox” programmes.",
                  "Unregulated herbal remedies.",
                  "High-dose supplements without a documented indication.",
                  "Abruptly stopping prescribed medicines.",
                  "Using a falling bile-acid result as permission to ignore the previous peak value.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Some herbal or “natural” preparations can themselves cause
                liver injury or interact with medicines.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                When Nutrition Alone Is Not Enough
              </h3>
              <div className="prose-rpl mt-4">
                <p>Nutrition should never delay:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Repeat bile-acid testing.",
                  "Investigation of jaundice or abdominal pain.",
                  "Assessment for pre-eclampsia.",
                  "Review of reduced fetal movements.",
                  "Treatment of significant sleep deprivation or itching.",
                  "Ursodeoxycholic acid when clinically indicated.",
                  "Specialist review of severe or early-onset disease.",
                  "Delivery recommended because of the peak bile-acid level or another maternal–fetal risk.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    My approach is diet-led, not diet-blinded. Nutrition is
                    used actively, while every fetal-safety threshold remains
                    visible.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Why a General Website Cannot Prescribe One Meal Plan
              </h3>
              <div className="prose-rpl mt-4">
                <p>The same food plan is not appropriate for:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "A woman with bile acids of 24 µmol/L and normal glucose.",
                  "A woman with bile acids of 110 µmol/L and gestational diabetes.",
                  "A woman carrying twins.",
                  "A woman with gallstones.",
                  "A woman who is underweight or unable to eat adequately.",
                  "A woman with severe reflux, vomiting or anaemia.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The detailed meal strategy belongs in an individual
                  consultation.
                </p>
                <p>
                  Separate educational articles may explain practical food
                  choices, meal timing and sample Indian dietary patterns
                  without suggesting that one menu can treat every pregnancy.
                </p>
              </div>

              {/* Ursodeoxycholic Acid */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Ursodeoxycholic Acid
              </h3>
              <div className="prose-rpl mt-4">
                <p>Ursodeoxycholic acid is commonly shortened to:</p>
                <p>
                  <strong>UDCA</strong>
                </p>
                <p>
                  SMFM recommends it as the first-line medicine for maternal
                  symptoms of ICP.
                </p>
                <p>It may:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Reduce itching modestly in some women.",
                  "Improve some liver-test results.",
                  "Possibly reduce spontaneous preterm birth in selected women, particularly when bile acids are 40 µmol/L or more.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>It has not been proved to prevent every adverse fetal outcome.</p>
                <p>It has not been proved to prevent stillbirth.</p>
                <p>
                  The large PITCHES randomised trial did not demonstrate a
                  significant reduction in its primary composite perinatal
                  outcome.
                </p>
                <p>
                  A subsequent individual-participant-data meta-analysis
                  suggested a possible reduction in preterm birth in selected
                  singleton pregnancies.
                </p>
                <p>The evidence must therefore be explained accurately:</p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    UDCA is principally used for maternal symptoms. It is not
                    a substitute for bile-acid surveillance or appropriately
                    timed birth.
                  </strong>{" "}
                  <Cite>3,4,7,8</Cite>
                </p>
              </div>

              <div className="prose-rpl mt-6">
                <p>
                  UDCA and nutrition should not be presented as competing
                  philosophies.
                </p>
                <p>In my practice, dietary management remains foundational.</p>
                <p>
                  UDCA is added when maternal symptoms, the biochemical
                  pattern or individual clinical circumstances justify it.
                </p>
                <p>
                  Improvement after either intervention must still be
                  interpreted against the highest bile-acid concentration
                  reached during the pregnancy.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-10">
                Dose and Monitoring
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  The preparation, dose and schedule should be prescribed
                  individually.
                </p>
                <p>The decision may consider:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Maternal weight.",
                  "Symptom severity.",
                  "Bile-acid level.",
                  "Treatment response.",
                  "Gastrointestinal side effects.",
                  "Other maternal medicines.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Patients should not alter the dose or stop treatment solely
                because one result improves.
              </p>

              {/* Additional Measures for Itching */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Additional Measures for Itching
              </h3>
              <div className="prose-rpl mt-4">
                <p>Supportive measures may include:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Cool baths or showers.",
                  "Loose cotton clothing.",
                  "Fragrance-free moisturisers.",
                  "Menthol-containing emollients.",
                  "Keeping the bedroom cool.",
                  "Antihistamines when clinically appropriate, particularly when they help sleep.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>These measures may improve comfort.</p>
                <p>
                  They do not reduce the fetal risk associated with severe
                  bile-acid elevation.
                </p>
              </div>

              {/* Vitamin K */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Vitamin K
              </h3>
              <div className="prose-rpl mt-4">
                <p>Most women with ICP do not require vitamin K.</p>
                <p>
                  It may be considered in selected women when there is:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Abnormal coagulation.",
                  "Significant jaundice.",
                  "Fat malabsorption.",
                  "Pale stools.",
                  "Another reason to suspect deficiency.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Vitamin K should not be prescribed automatically to every
                woman with ICP.
              </p>

              {/* Rifampicin */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Rifampicin
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Rifampicin has been used in specialist settings for severe
                  symptoms or biochemical disease that has not responded
                  adequately to first-line management.
                </p>
                <p>Evidence remains limited.</p>
                <p>It requires specialist supervision because of:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Maternal monitoring.",
                  "Drug interactions.",
                  "Possible liver effects.",
                  "Neonatal and bleeding considerations.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                It is not routine first-line treatment.
              </p>

              {/* Treatments Not Recommended Routinely */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Treatments Not Recommended Routinely
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The following should not be promoted as proven fetal-
                  protection treatments:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Herbal liver cleanses.",
                  "Detox diets.",
                  "Cholestyramine for routine ICP management.",
                  "S-adenosyl methionine as standard treatment.",
                  "Dexamethasone as treatment for maternal cholestasis.",
                  "Repeated steroid courses intended to lower bile acids.",
                  "Aspirin solely because ICP is present.",
                  "Heparin solely because ICP is present.",
                  "Routine plasma exchange.",
                  "Routine early delivery before laboratory confirmation of elevated bile acids.",
                  "Home fetal-heart monitoring as reassurance.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Antenatal corticosteroids may still be recommended for{" "}
                  <strong>fetal lung maturation</strong> when medically
                  indicated preterm delivery is planned.
                </p>
                <p>That is different from using steroids to treat ICP itself.</p>
              </div>
            </section>

            {/* --- PART 3 END --------------------------------------- */}

            {/* --- PART 4 START ------------------------------------- */}

            {/* Timing of Birth */}
            <section id="timing-of-birth" className="scroll-mt-32 mt-16">
              <Overline>Delivery Planning</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Timing of Birth
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Most Important Decision Is Not Always a Prescription
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  In severe ICP, fetal-risk reduction depends strongly on
                  deciding when the baby may be safer outside the uterus than
                  inside it.
                </p>
                <p>The decision must balance:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Stillbirth risk.",
                  "Prematurity.",
                  "Peak bile-acid level.",
                  "Gestational age.",
                  "Other pregnancy complications.",
                  "Previous pregnancy history.",
                  "Local neonatal facilities.",
                  "The woman’s informed preference.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Why Guidelines Differ
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  International guidelines interpret the same evidence within
                  different healthcare systems.
                </p>
                <p>Their recommendations are close, but not identical.</p>
                <p>This difference should be explained rather than hidden.</p>
              </div>

              {/* RCOG */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-12">
                RCOG Approach for an Otherwise Uncomplicated Singleton Pregnancy
              </h3>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Peak Bile Acids 19–39 µmol/L
              </h4>
              <div className="prose-rpl mt-4">
                <p>Planned birth by the estimated due date may be considered.</p>
                <p>
                  If there are no other risk factors, waiting for spontaneous
                  labour may also be reasonable.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Peak Bile Acids 40–99 µmol/L
              </h4>
              <div className="prose-rpl mt-4">
                <p>Planned birth at approximately:</p>
                <p>
                  <strong>38–39 weeks</strong>
                </p>
                <p>may be recommended.</p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Peak Bile Acids 100 µmol/L or More
              </h4>
              <div className="prose-rpl mt-4">
                <p>Planned birth at approximately:</p>
                <p>
                  <strong>35–36 weeks</strong>
                </p>
                <p>
                  may be recommended.<Cite>1,2</Cite>
                </p>
              </div>

              {/* SMFM and ACOG */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-12">
                SMFM and ACOG-Endorsed Approach
              </h3>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Bile Acids 100 µmol/L or More
              </h4>
              <div className="prose-rpl mt-4">
                <p>Offer delivery at:</p>
                <p>
                  <strong>36 weeks 0 days</strong>
                </p>
                <p>
                  because the stillbirth risk rises substantially around this
                  gestational age.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Bile Acids Below 100 µmol/L
              </h4>
              <div className="prose-rpl mt-4">
                <p>Plan delivery within:</p>
                <p>
                  <strong>36 weeks 0 days to 39 weeks 0 days</strong>
                </p>
                <p>The exact point within this range is individualised.</p>
                <p>
                  Women with levels below 40 µmol/L are generally placed
                  toward the later end.
                </p>
                <p>
                  Women with levels of 40–99 µmol/L may be placed toward the
                  earlier end.<Cite>3</Cite>
                </p>
              </div>

              {/* FIGO */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-12">
                FIGO 2025 Approach
              </h3>
              <div className="prose-rpl mt-4">
                <p>FIGO recommends:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Regular non-fasting bile-acid measurement.",
                  "Recognition that risk increases substantially at 100 µmol/L or more.",
                  { text: "Consideration of elective delivery at approximately ", strongMid: "35–36 weeks", after: " when the level reaches this severe range.", cite: "4" },
                ].map((item, i) => {
                  if (typeof item === "string") {
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <span
                          className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                          aria-hidden="true"
                        />
                        <span>{item}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                        aria-hidden="true"
                      />
                      <span>
                        {item.text}
                        <strong>{item.strongMid}</strong>
                        {item.after}
                        <Cite>{item.cite}</Cite>
                      </span>
                    </li>
                  );
                })}
              </ul>

              {/* Dr Tania's Clinical Interpretation */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-12">
                Dr Tania’s Clinical Interpretation
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The safest date should not be selected by copying one
                  number from one chart.
                </p>
                <p>The plan should account for:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The highest bile-acid concentration.",
                  "How rapidly the level rose.",
                  "The response to structured nutrition and treatment.",
                  "Current gestational age.",
                  "Previous ICP severity.",
                  "Previous ICP-related fetal loss.",
                  "Singleton or multiple pregnancy.",
                  "Pre-eclampsia.",
                  "Gestational diabetes.",
                  "Fetal growth.",
                  "Maternal liver function.",
                  "Severity of unrelenting symptoms.",
                  "Neonatal capability.",
                  "Risks of waiting compared with the risks of prematurity.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The delivery date is not chosen because the itching is
                    unbearable alone. It is chosen after the maternal and
                    fetal risks have been weighed together.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Delivery Before 35–36 Weeks
              </h3>
              <div className="prose-rpl mt-4">
                <p>Earlier delivery may be considered in exceptional circumstances, including:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Another maternal or fetal indication.",
                  "Worsening liver disease.",
                  "Severe and unremitting symptoms despite treatment.",
                  "A previous ICP-related stillbirth before 36 weeks.",
                  "Non-reassuring fetal assessment.",
                  "Pre-eclampsia or another major complication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                This requires specialist decision-making.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Do Not Deliver Early for Itching Alone Without Confirmation
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  SMFM recommends against delivery before 37 weeks for a
                  clinical diagnosis of ICP when elevated bile acids have not
                  been laboratory-confirmed.
                </p>
                <p>Persistent itching still deserves:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Repeat testing.",
                  "Assessment for another cause.",
                  "An individualised plan.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Antenatal Corticosteroids
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  When delivery before 37 weeks is planned and an appropriate
                  course has not previously been given, antenatal
                  corticosteroids may be recommended for fetal lung
                  maturation.
                </p>
                <p>Steroids do not treat or cure ICP.</p>
              </div>
            </section>

            {/* Labour and Newborn Care */}
            <section id="labour-and-newborn-care" className="scroll-mt-32 mt-16">
              <Overline>Labour & Newborn</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Labour and Newborn Care
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Induction Does Not Mean Caesarean Birth
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP alone does not require a planned caesarean birth.</p>
                <p>Options may include:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Induction of labour.",
                  "Spontaneous labour when appropriate.",
                  "Planned caesarean for a separate obstetric indication.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The method of birth depends on:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Gestational age.",
                  "Cervical findings.",
                  "Fetal presentation.",
                  "Previous births.",
                  "Previous caesarean.",
                  "Fetal wellbeing.",
                  "Maternal preference.",
                  "Standard obstetric indications.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fetal Monitoring During Labour
              </h3>
              <div className="prose-rpl mt-4">
                <p>Continuous CTG may be advised when:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Bile acids reached 100 µmol/L or more.",
                  "There are additional pregnancy risks.",
                  "Labour is induced preterm.",
                  "Meconium is present.",
                  "Another clinical concern develops.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Meconium
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The delivery team should be aware of the increased
                  likelihood of meconium-stained fluid.
                </p>
                <p>
                  The newborn team should be available according to
                  gestational age and clinical circumstances.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Neonatal Care
              </h3>
              <div className="prose-rpl mt-4">
                <p>The baby may require:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Routine observation.",
                  "Assessment for prematurity.",
                  "Respiratory support.",
                  "Glucose monitoring.",
                  "Observation after meconium exposure.",
                  "Neonatal-unit admission when indicated.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Most babies do well.</p>
                <p>Neonatal needs are more often related to:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Gestational age at birth.",
                  "Other pregnancy complications.",
                  "Meconium or breathing concerns.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Breastfeeding
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP does not usually prevent breastfeeding.</p>
                <p>
                  The maternal medication plan should still be reviewed after
                  delivery.
                </p>
              </div>
            </section>

            {/* The Next Pregnancy */}
            <section id="the-next-pregnancy" className="scroll-mt-32 mt-16">
              <Overline>Preconception & Recurrence</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Next Pregnancy Begins Before the Positive Test
              </h2>
              <div className="prose-rpl mt-5">
                <p>ICP usually improves after delivery.</p>
                <p>Its history remains important.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Before Another Pregnancy
              </h3>
              <div className="prose-rpl mt-4">
                <p>The plan may include:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Confirming that previous liver tests returned to normal.",
                  "Reviewing the peak bile-acid concentration.",
                  "Recording the gestational age at diagnosis.",
                  "Reviewing the timing and reason for delivery.",
                  "Identifying an underlying liver or gallbladder condition.",
                  "Reviewing medicines and contraception.",
                  "Arranging early-pregnancy baseline testing.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                At the Beginning of the Next Pregnancy
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  RCOG advises checking liver-function tests and bile acids
                  at the beginning of future pregnancies.
                </p>
                <p>
                  The woman should inform her clinician about the previous
                  ICP even when she is not yet itching.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                If Itching Returns
              </h3>
              <div className="prose-rpl mt-4">
                <p>Bile acids and liver tests should be checked promptly.</p>
                <p>
                  A normal result early in pregnancy does not exclude
                  recurrence later.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Recurrence
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP commonly recurs.</p>
                <p>The next pregnancy may:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Begin at a similar gestational stage.",
                  "Begin earlier.",
                  "Be milder.",
                  "Be more severe.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The previous pattern guides vigilance but does not perfectly
                predict the next pregnancy.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Contraception
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  After bile acids and liver tests have returned to normal,
                  ICP does not automatically prohibit hormonal contraception.
                </p>
                <p>
                  If an oestrogen-containing contraceptive triggers itching
                  or abnormal liver tests, medical review is required.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Long-Term Liver Health
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Persistent or recurrent abnormal tests outside pregnancy
                  may indicate an underlying liver or biliary condition.
                </p>
                <p>
                  This deserves assessment rather than being labelled
                  indefinitely as pregnancy cholestasis.
                </p>
              </div>

              {/* After Birth: Confirm That It Has Ended */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                After Birth: Confirm That It Has Ended
              </h3>
              <div className="prose-rpl mt-4">
                <p>The itching usually improves rapidly after birth.</p>
                <p>
                  The laboratory results may take several weeks to normalise.
                </p>
                <p>
                  At approximately six weeks postpartum, the clinician should
                  confirm:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The itching has resolved.",
                  "Liver-function tests have returned to normal.",
                  "Bile acids have returned to normal.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Further investigation or hepatology referral may be required
                when:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Itching persists.",
                  "Bile acids remain elevated.",
                  "Liver enzymes remain abnormal.",
                  "Jaundice persists.",
                  "Symptoms recur outside pregnancy.",
                  { text: "There is a strong family or medical history.", cite: "2,10" },
                ].map((item, i) => {
                  if (typeof item === "string") {
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <span
                          className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                          aria-hidden="true"
                        />
                        <span>{item}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                        aria-hidden="true"
                      />
                      <span>
                        {item.text}
                        <Cite>{item.cite}</Cite>
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The diagnosis is completed only when the pregnancy-
                    related abnormality resolves after birth.
                  </strong>
                </p>
              </div>

              {/* Supportive Care Is Part of Medical Care */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Supportive Care Is Part of Medical Care
              </h3>
              <div className="prose-rpl mt-4">
                <p>ICP can be exhausting.</p>
                <p>The itching may prevent sleep for weeks.</p>
                <p>Fear may increase each time the bile-acid report rises.</p>
                <p>A woman may be repeatedly told:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "“The scan is normal.”",
                  "“The CTG is normal.”",
                  "“Try not to worry.”",
                  "“It is only itching.”",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                That is not enough.
              </p>
              <p className="mt-3 text-[15px] text-stone2 leading-relaxed">
                Supportive care includes:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "A clear explanation of the current risk category.",
                  "A written schedule for repeat blood tests.",
                  "A direct route for reduced fetal movements.",
                  "Honest explanation of the limitations of CTG and ultrasound.",
                  "A provisional delivery window.",
                  "Review after every significant bile-acid rise.",
                  "Help with severe sleep disturbance.",
                  "Psychological support when anxiety becomes overwhelming.",
                  "Inclusion of the partner in major discussions.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    Reassurance should come from a plan—not from pretending
                    that the risk does not exist.
                  </strong>
                </p>
              </div>

              {/* Hope Must Be Personal, Not Careless */}
              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-14">
                Hope Must Be Personal, Not Careless
              </h3>
              <div className="prose-rpl mt-4">
                <p>Most women with ICP have a healthy baby.</p>
                <p>The prognosis depends on:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The highest bile-acid concentration.",
                  "Gestational age when it rose.",
                  "Singleton or multiple pregnancy.",
                  "Other maternal complications.",
                  "Fetal condition.",
                  "Timing of birth.",
                  "Neonatal facilities.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-10">
                When Bile Acids Remain Below 40 µmol/L
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  The stillbirth risk in an otherwise uncomplicated singleton
                  pregnancy appears similar to background risk.
                </p>
                <p>
                  Repeat testing remains important because the level may rise.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                When Bile Acids Reach 40–99 µmol/L
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  The risk of spontaneous preterm birth and meconium becomes
                  more relevant.
                </p>
                <p>A planned birth date is usually discussed before late term.</p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                When Bile Acids Reach 100 µmol/L or More
              </h4>
              <div className="prose-rpl mt-4">
                <p>The fetal-risk category changes.</p>
                <p>
                  Planned birth at approximately 35–36 weeks or 36 weeks—
                  depending on the guideline and individual situation—is a
                  central preventive strategy.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-10">
                What Timely Care Can Achieve
              </h4>
              <div className="prose-rpl mt-4">
                <p>Timely care can:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Introduce structured nutrition early.",
                  "Identify women whose symptoms and biochemical pattern respond to dietary management.",
                  "Maintain adequate pregnancy nutrition while reducing avoidable dietary stress.",
                  "Recognise promptly when nutrition and symptom treatment are no longer sufficient.",
                  "Detect biochemical progression.",
                  "Recognise severe disease.",
                  "Avoid relying on symptoms alone.",
                  "Avoid relying on liver enzymes alone.",
                  "Avoid waiting for a CTG to predict an unpredictable event.",
                  "Balance prematurity against stillbirth risk.",
                  "Plan birth in an appropriate hospital.",
                  "Prepare the neonatal team.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                No clinician can guarantee the outcome.
              </p>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The strongest care plan does not choose between nutrition
                    and evidence-based obstetrics. It uses both.
                  </strong>
                </p>
              </div>
            </section>

            {/* --- PART 4 END --------------------------------------- */}

            {/* --- PART 5 START ------------------------------------- */}

            {/* Frequently Asked Questions */}
            <section id="faq" className="scroll-mt-32 mt-16">
              <Overline>FAQ</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Frequently Asked Questions
              </h2>
              <div className="mt-8" data-testid="ihcp-faq-list">
                {FAQ_ITEMS.map((f, i) => (
                  <FaqRow key={i} q={f.q} a={f.a} />
                ))}
              </div>
            </section>

            {/* Closing Section */}
            <section id="closing" className="scroll-mt-32 mt-16">
              <Overline>Closing</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                A High Bile-Acid Result Is Not a Reason to Panic. It Is a
                Reason to Build a Complete Plan.
              </h2>
              <div className="prose-rpl mt-6">
                <p>The itching raises suspicion.</p>
                <p>The blood test confirms and grades the condition.</p>
                <p>
                  Nutrition becomes an active part of maternal management.
                </p>
                <p>
                  The response to diet and treatment is followed carefully.
                </p>
                <p>
                  The highest bile-acid level still helps define fetal risk.
                </p>
                <p>
                  A normal CTG does not erase severe biochemical disease.
                </p>
                <p>A falling result does not erase a previous peak.</p>
                <p>Ursodeoxycholic acid may improve symptoms.</p>
                <p>
                  Neither medicine nor diet replaces appropriately timed birth
                  when the pregnancy enters a higher-risk category.
                </p>
                <p>
                  The objective is to enter the remainder of the pregnancy
                  with:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The diagnosis confirmed accurately.",
                  "Other liver conditions excluded when necessary.",
                  "A personalised nutrition plan that remains adequate for pregnancy.",
                  "The highest bile-acid level identified.",
                  "Repeat testing scheduled.",
                  "Associated pregnancy risks assessed.",
                  "Fetal-movement guidance understood.",
                  "The limitations of surveillance explained.",
                  "A delivery window agreed before the risk rises.",
                  "Neonatal care available when early birth is required.",
                  "Postpartum resolution confirmed.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 bg-navy text-white p-8">
                <div className="font-serif text-2xl leading-tight">
                  Schedule a High Bile Acids Consultation
                </div>
                <p className="text-white/70 text-sm mt-3 leading-relaxed">
                  Bring every bile-acid report. Leave with a nutrition,
                  monitoring and delivery plan.
                </p>
                <div className="mt-5">
                  <PrimaryButton
                    to="/contact?type=appointment&reason=ihcp"
                    testid="ihcp-closing-cta"
                  >
                    Schedule a High Bile Acids Consultation
                  </PrimaryButton>
                </div>
              </div>
            </section>

            {/* Medical Disclaimer */}
            <section id="disclaimer" className="scroll-mt-32 mt-16">
              <Overline>Medical Disclaimer</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Medical Disclaimer
              </h2>
              <div className="prose-rpl mt-6" data-testid="ihcp-disclaimer">
                <p>
                  This page provides general educational information about
                  high bile acids and intrahepatic cholestasis of pregnancy.
                </p>
                <p>
                  It does not replace individual medical assessment,
                  nutritional assessment, laboratory interpretation,
                  diagnosis, treatment, fetal surveillance or delivery
                  planning.
                </p>
                <p>
                  Bile-acid thresholds and delivery recommendations must be
                  interpreted according to gestational age, peak
                  concentration, laboratory method, singleton or multiple
                  pregnancy, previous history and other maternal or fetal
                  complications.
                </p>
                <p>
                  Reduced fetal movements, absent fetal movements,
                  significant bleeding, contractions, fluid leakage,
                  jaundice, dark urine, pale stools, severe abdominal pain,
                  severe headache, visual disturbance, fainting,
                  breathlessness or feeling seriously unwell may require
                  urgent medical assessment.
                </p>
              </div>
            </section>

            {/* Evidence Base */}
            <section id="evidence-base" className="scroll-mt-32 mt-16">
              <Overline>Evidence Base</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Evidence Base
              </h2>
              <ol
                className="mt-8 space-y-5 text-[14px] text-stone2 leading-relaxed"
                data-testid="ihcp-evidence-base"
              >
                <li id="ref-1" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[1]</span>
                  Girling J, Knight CL, Chappell LC; Royal College of
                  Obstetricians and Gynaecologists. Intrahepatic cholestasis
                  of pregnancy: Green-top Guideline No. 43.{" "}
                  <em>BJOG.</em> 2022;129(13):e95–e114.
                  doi:10.1111/1471-0528.17206.
                </li>
                <li id="ref-2" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[2]</span>
                  Royal College of Obstetricians and Gynaecologists.{" "}
                  <em>
                    Intrahepatic Cholestasis of Pregnancy: Patient
                    Information.
                  </em>{" "}
                  First published 2022; updated April 2026.
                </li>
                <li id="ref-3" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[3]</span>
                  Society for Maternal-Fetal Medicine. Consult Series No. 53:
                  Intrahepatic cholestasis of pregnancy.{" "}
                  <em>
                    American Journal of Obstetrics and Gynecology.
                  </em>{" "}
                  2021;224(2):B2–B9. Reaffirmed 2024.
                  doi:10.1016/j.ajog.2020.11.002.
                </li>
                <li id="ref-4" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[4]</span>
                  Nana M, Medina V, Maxwell C, et al. FIGO guideline on
                  liver disease and pregnancy.{" "}
                  <em>
                    International Journal of Gynecology &amp; Obstetrics.
                  </em>{" "}
                  2025;170(1):28–48. doi:10.1002/ijgo.70161.
                </li>
                <li id="ref-5" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[5]</span>
                  Hobson SR, Cohen ER, Gandhi S, et al. Guideline No. 452:
                  Diagnosis and Management of Intrahepatic Cholestasis of
                  Pregnancy.{" "}
                  <em>
                    Journal of Obstetrics and Gynaecology Canada.
                  </em>{" "}
                  2024;46(8):102618. doi:10.1016/j.jogc.2024.102618.
                </li>
                <li id="ref-6" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[6]</span>
                  Ovadia C, Seed PT, Sklavounos A, et al. Association of
                  adverse perinatal outcomes of intrahepatic cholestasis of
                  pregnancy with biochemical markers: aggregate and
                  individual-patient-data meta-analyses.{" "}
                  <em>The Lancet.</em> 2019;393(10174):899–909.
                  doi:10.1016/S0140-6736(18)31877-4.
                </li>
                <li id="ref-7" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[7]</span>
                  Chappell LC, Bell JL, Smith A, et al. Ursodeoxycholic acid
                  versus placebo in women with intrahepatic cholestasis of
                  pregnancy: the PITCHES randomised controlled trial.{" "}
                  <em>The Lancet.</em> 2019;394(10201):849–860.
                  doi:10.1016/S0140-6736(19)31270-X.
                </li>
                <li id="ref-8" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[8]</span>
                  Ovadia C, Sajous J, Seed PT, et al. Ursodeoxycholic acid
                  in intrahepatic cholestasis of pregnancy: a systematic
                  review and individual-participant-data meta-analysis.{" "}
                  <em>The Lancet Gastroenterology &amp; Hepatology.</em>{" "}
                  2021;6(7):547–558. doi:10.1016/S2468-1253(21)00074-1.
                </li>
                <li id="ref-9" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[9]</span>
                  Hague WM, Briley A, Callaway L, et al. Intrahepatic
                  cholestasis of pregnancy—diagnosis and management: a
                  SOMANZ consensus statement executive summary.{" "}
                  <em>
                    Australian and New Zealand Journal of Obstetrics and
                    Gynaecology.
                  </em>{" "}
                  2023;63(5):656–665. doi:10.1111/ajo.13719.
                </li>
                <li id="ref-10" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[10]</span>
                  European Association for the Study of the Liver. EASL
                  Clinical Practice Guidelines on the management of liver
                  diseases in pregnancy.{" "}
                  <em>Journal of Hepatology.</em> 2023;79(3):768–828.
                  doi:10.1016/j.jhep.2023.03.006.
                </li>
                <li id="ref-11" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[11]</span>
                  Riikonen S, Savonius H, Gylling H, Nikkilä K, Tuomi AM,
                  Miettinen TA. Oral guar gum, a gel-forming dietary fiber,
                  relieves pruritus in intrahepatic cholestasis of pregnancy.{" "}
                  <em>
                    Acta Obstetricia et Gynecologica Scandinavica.
                  </em>{" "}
                  2000;79(4):260–264. PMID: 10746839.
                </li>
                <li id="ref-12" className="scroll-mt-32">
                  <span className="mono text-[11px] text-prestige mr-2">[12]</span>
                  Walker KF, Chappell LC, Hague WM, Middleton P, Thornton
                  JG. Pharmacological interventions for treating
                  intrahepatic cholestasis of pregnancy.{" "}
                  <em>
                    Cochrane Database of Systematic Reviews.
                  </em>{" "}
                  2020;7:CD000493. doi:10.1002/14651858.CD000493.pub3.
                </li>
              </ol>
            </section>

            {/* --- PART 5 END --------------------------------------- */}
          </div>
        </div>
      </section>
    </div>
  );
}
